import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useSearch } from 'wouter';

import { StoragePanel } from '../../components/Settings/StoragePanel';
import { SubscriptionsPanel } from '../../components/Settings/SubscriptionsPanel';
import { BridgeDownloadCard } from '../../components/Settings/BridgeDownloadCard';
// LA-12: the one intake component (LA-11), mounted inside the EXISTING Local Node
// details block. No new step, route or goal answer (LA-12/LA-13).
import { LocalAiIntake, dispatchLocalAiPlan } from '../../components/Settings/LocalAiIntake';
import { ProviderKeysPanel } from '../ProviderKeys/ProviderKeysPage';
import { GettingStartedOverview } from '../../components/Settings/ProviderSetupGuide';
import { listProviderKeys } from '../ProviderKeys/providerKeysApi';
import { getStoragePreferences } from '../../api/storage';
import { getTermsAcceptance } from '../../api/legal';
import { fetchDevices } from '../../api/operations';
import {
  checkoutStatus, consumePendingCheckoutSessionRef, fetchBillingState, rememberPendingCheckoutSessionRef,
} from '../../api/billing';
import { TermsAcceptancePanel } from '../../components/TermsAcceptancePanel';
import { SessionContext } from '../../session';
import {
  MISSING_ADVICE,
  WELCOME_GOALS,
  WELCOME_TOOLS,
  missingCapabilities,
  neededCapabilities,
  readWelcomeAnswers,
  recommendedProducts,
  saveWelcomeAnswers,
  subscriptionsRelevant,
  type WelcomeAnswers,
} from '../../lib/welcomeAnswers';
import { ChooseProductStep } from './ChooseProductStep';
import { clearPendingOffer, getPendingOffer } from '../../lib/pendingOffer';

/** The keys panel takes the questionnaire's capability ids to show the relevant groups first (F-26). */
const KeysPanel = ProviderKeysPanel as unknown as (props: {
  focusCapabilities?: string[]; onDone?: () => void;
}) => React.ReactElement;

/**
 * Welcome (owner decisions F-25 and F-40, 2026-10-08). The order is:
 *
 *   terms → "what are you here for?" → choose a product and pay → storage → API keys → subscriptions → done
 *
 * Nobody is asked for an API key before choosing a product. The questionnaire's answers decide what each
 * later step shows first (the recommended product, the key groups that matter, whether the
 * subscriptions step applies); everything stays available in Settings.
 *
 * The current step lives in the URL (`/welcome?step=keys`), so browser Back and Forward move between
 * steps, a sub-page opened from a step comes back to it, and every step has Back plus a clickable step
 * list (F-12, F-14, F-17). Finished steps stay reachable and editable.
 */
export type OnboardingStep = 'goals' | 'plan' | 'storage' | 'keys' | 'subscriptions' | 'done';
export type OnboardingStage = 'loading' | 'terms' | OnboardingStep;

/** One server-derived status per setup area. `unknown` means the read failed. */
export type SetupStatus = 'complete' | 'incomplete' | 'unknown';
export interface OnboardingSnapshot {
  terms: SetupStatus;
  /** The questionnaire was answered or skipped (this browser's record). */
  goals: SetupStatus;
  /** A plan is active, or a choice was made on the product step. */
  plan: SetupStatus;
  storage: SetupStatus;
  keys: SetupStatus;
  devices: SetupStatus;
}

/** Facts the final summary shows (F-20). */
export interface OnboardingDetails {
  termsVersion: string | null;
  subscribed: boolean;
  /** A plan picked before sign-up that differs from the plan in force (a switch request). */
  switchRequested?: boolean;
  keyProviders: string[];
  answers: WelcomeAnswers | null;
  /**
   * LA-12: the paired Local Nodes the server already reports. The subscriptions step shows the
   * local AI intake for the first of them; this is a projection of the existing devices read,
   * not a new one. The snapshot and the resume logic do not depend on it.
   */
  pairedDeviceIds?: string[];
}

/** The existing server reads the snapshot is derived from; injectable for tests. */
export interface OnboardingSources {
  terms: () => Promise<{ accepted: boolean; currentVersion?: string }>;
  storage: () => Promise<{ defaults_confirmed_at: string | null }>;
  keys: () => Promise<unknown>;
  devices: () => Promise<{ devices?: { device_id?: string; status?: string }[] }>;
  billing: () => Promise<{ subscribed: boolean; status?: string; paid_service?: boolean; offer_id?: string }>;
  answers: () => Promise<WelcomeAnswers | null>;
}

const SERVER_SOURCES: Omit<OnboardingSources, 'answers'> = {
  terms: getTermsAcceptance,
  storage: () => getStoragePreferences(),
  keys: listProviderKeys,
  devices: fetchDevices,
  billing: fetchBillingState,
};

/**
 * A plan counts only while it is in force: `subscribed` alone is true for any subscription record,
 * including a cancelled one, so the lifecycle and paid-service projection decide.
 */
export function planInForce(billing: { subscribed: boolean; status?: string; paid_service?: boolean } | null | undefined): boolean {
  if (!billing?.subscribed) return false;
  return billing.paid_service === true || billing.status === 'active' || billing.status === 'trialing';
}

const UNKNOWN: OnboardingSnapshot = {
  terms: 'unknown', goals: 'unknown', plan: 'unknown', storage: 'unknown', keys: 'unknown', devices: 'unknown',
};

function settledStatus<T>(result: PromiseSettledResult<T>, complete: (value: T) => boolean): SetupStatus {
  if (result.status !== 'fulfilled') return 'unknown';
  try { return complete(result.value) ? 'complete' : 'incomplete'; } catch { return 'unknown'; }
}

function storedKeyProviders(payload: unknown): string[] {
  const rows = (payload as { provider_keys?: unknown } | null)?.provider_keys;
  if (!Array.isArray(rows)) throw new Error('malformed provider keys');
  return rows.flatMap((row) => {
    const key = row as { key_id?: unknown; status?: unknown; revoked_at?: unknown; provider?: unknown };
    const live = typeof key.key_id === 'string' && key.key_id !== '' && key.status !== 'revoked' && !key.revoked_at;
    return live ? [typeof key.provider === 'string' ? key.provider : 'provider'] : [];
  });
}

/**
 * IN-11: derive first-run progress from the durable server records (terms acceptance, billing state,
 * storage defaults, stored provider keys, paired devices) plus this browser's questionnaire record, so a
 * reload resumes where the customer left off. A failed read is `unknown`, never `complete`: the step is
 * shown again rather than silently skipped.
 */
export async function loadOnboardingSnapshot(sources: OnboardingSources): Promise<OnboardingSnapshot> {
  return (await loadOnboarding(sources)).snapshot;
}

export async function loadOnboarding(sources: OnboardingSources): Promise<{ snapshot: OnboardingSnapshot; details: OnboardingDetails }> {
  const [terms, storage, keys, devices, billing, answers] = await Promise.allSettled([
    sources.terms(), sources.storage(), sources.keys(), sources.devices(), sources.billing(), sources.answers(),
  ]);
  const savedAnswers = answers.status === 'fulfilled' ? answers.value : null;
  const billingValue = billing.status === 'fulfilled' ? billing.value : null;
  const subscribed = planInForce(billingValue);
  // A plan picked before sign-up (`/signup?offer=…`) reopens the product step until it is bought, or
  // until an existing subscriber decides about switching.
  const pending = getPendingOffer();
  const switchRequested = Boolean(subscribed && pending && billingValue?.offer_id && pending.id !== billingValue.offer_id);
  const pendingPurchase = pending !== null && (!subscribed || switchRequested);
  // Only a plan in force or an explicit "decide later" completes the step; a started checkout does not.
  let plan: SetupStatus = !pendingPurchase && (subscribed || savedAnswers?.plan === 'later') ? 'complete' : 'incomplete';
  if (plan !== 'complete' && billing.status !== 'fulfilled') plan = 'unknown';
  let keyProviders: string[] = [];
  const keysStatus = settledStatus(keys, (value) => { keyProviders = storedKeyProviders(value); return keyProviders.length > 0; });
  // LA-12: the paired Local Node ids, read by the existing devices call above.
  let pairedDeviceIds: string[] = [];
  if (devices.status === 'fulfilled') {
    const reported = devices.value.devices;
    if (Array.isArray(reported)) {
      pairedDeviceIds = reported
        .filter((device) => device.status === 'active' && typeof device.device_id === 'string' && device.device_id !== '')
        .map((device) => device.device_id as string);
    }
  }
  return {
    snapshot: {
      terms: settledStatus(terms, (value) => value.accepted === true),
      goals: savedAnswers ? 'complete' : (answers.status === 'fulfilled' ? 'incomplete' : 'unknown'),
      plan,
      storage: settledStatus(storage, (value) => Boolean(value.defaults_confirmed_at)),
      keys: keysStatus,
      devices: settledStatus(devices, (value) => {
        if (!Array.isArray(value.devices)) throw new Error('malformed devices');
        return value.devices.some((device) => device.status === 'active');
      }),
    },
    details: {
      termsVersion: terms.status === 'fulfilled' && terms.value.accepted ? (terms.value.currentVersion ?? null) : null,
      subscribed,
      switchRequested,
      keyProviders,
      answers: savedAnswers,
      pairedDeviceIds,
    },
  };
}

const ORDER: readonly OnboardingStep[] = ['goals', 'plan', 'storage', 'keys', 'subscriptions', 'done'];
const STEP_STATUS: Record<Exclude<OnboardingStep, 'done'>, keyof OnboardingSnapshot> = {
  goals: 'goals', plan: 'plan', storage: 'storage', keys: 'keys', subscriptions: 'devices',
};

/**
 * The first stage at or after `from` that is not complete. A step the answers make irrelevant
 * (subscriptions, for someone with no consumer AI subscription) is not stopped at.
 */
export function resumeStage(
  snapshot: OnboardingSnapshot, from: 'terms' | OnboardingStep = 'terms', skip: OnboardingStep[] = [],
): 'terms' | OnboardingStep {
  if (from === 'terms' && snapshot.terms !== 'complete') return 'terms';
  if (from === 'done') return 'done';
  const start = from === 'terms' ? 0 : ORDER.indexOf(from);
  for (const step of ORDER.slice(start)) {
    if (step === 'done') return 'done';
    if (skip.includes(step)) continue;
    if (snapshot[STEP_STATUS[step]] !== 'complete') return step;
  }
  return 'done';
}

const STEP_LABELS: Record<'terms' | OnboardingStep, string> = {
  terms: 'Terms',
  goals: 'About your work',
  plan: 'Choose your product',
  storage: 'Storage defaults',
  keys: 'API keys',
  subscriptions: 'Subscriptions',
  done: 'Done',
};

// Exactly the destinations of ENV_AND_DEEPLINK_CONTRACT.md section 4. The
// checkout flow needs no query parameters, so only the path is ever returned.
const SAFE_RETURN_TO_PATHS = new Set([
  '/billing/checkout',
  '/welcome',
  '/projects',
]);

export function safeReturnTo(search: string): string {
  const requested = new URLSearchParams(search).get('return_to');
  if (
    !requested ||
    !requested.startsWith('/') ||
    requested.startsWith('//') ||
    /[\\\u0000-\u001f\u007f]/.test(requested)
  ) {
    return '/projects';
  }
  const path = requested.split(/[?#]/, 1)[0];
  if (!SAFE_RETURN_TO_PATHS.has(path)) return '/projects';
  return path;
}

function isStep(value: string | null): value is OnboardingStep | 'terms' {
  return value === 'terms' || (ORDER as readonly string[]).includes(value || '');
}

/** The clickable step list: every step can be opened, finished ones are marked (F-12, F-14). */
export function OnboardingProgress({ step, snapshot, onSelect, skipped = [] }: {
  step: 'terms' | OnboardingStep;
  snapshot: OnboardingSnapshot | null;
  onSelect?: (step: 'terms' | OnboardingStep) => void;
  skipped?: OnboardingStep[];
}) {
  const items: ('terms' | OnboardingStep)[] = ['terms', ...ORDER];
  return (
    <ol className="onboarding-step-list" aria-label="Welcome steps">
      {items.map((item, index) => {
        const statusKey = item === 'done' ? null : item === 'terms' ? 'terms' : STEP_STATUS[item];
        const complete = Boolean(snapshot && statusKey && snapshot[statusKey] === 'complete');
        const suffix = complete ? ' (already set up)' : skipped.includes(item as OnboardingStep) ? ' (optional)' : '';
        const current = item === step;
        return (
          <li key={item} className={current ? 'current' : complete ? 'complete' : ''} aria-current={current ? 'step' : undefined}>
            {onSelect && !current ? (
              <button type="button" className="link-button" onClick={() => onSelect(item)}>
                <span>{index + 1}</span> {STEP_LABELS[item]}{suffix}
              </button>
            ) : <><span>{index + 1}</span> {STEP_LABELS[item]}{suffix}</>}
          </li>
        );
      })}
    </ol>
  );
}

function Chips({ legend, options, selected, onChange }: {
  legend: string; options: readonly { id: string; label: string }[]; selected: string[]; onChange: (next: string[]) => void;
}) {
  return (
    <fieldset className="welcome-chips">
      <legend>{legend}</legend>
      {options.map((option) => {
        const on = selected.includes(option.id);
        return (
          <button key={option.id} type="button" className="chip" aria-pressed={on}
            onClick={() => onChange(on ? selected.filter((id) => id !== option.id) : [...selected, option.id])}>
            {option.label}
          </button>
        );
      })}
    </fieldset>
  );
}

/** F-25: a few questions, then a plain recommendation of what is needed and what is missing. */
function GoalsStep({ initial, onDone }: { initial: WelcomeAnswers | null; onDone: (answers: WelcomeAnswers) => void }) {
  const [goals, setGoals] = useState<string[]>(initial?.goals ?? []);
  const [tools, setTools] = useState<string[]>(initial?.tools ?? []);
  const missing = missingCapabilities(goals, tools);
  return (
    <>
      <p>Two quick questions so we only show the setup you need. You can change everything later in Settings.</p>
      <Chips legend="What are you here to make? Pick any." options={WELCOME_GOALS} selected={goals} onChange={setGoals} />
      <Chips legend="Which AI tools or accounts do you already have or want to use?" options={WELCOME_TOOLS}
        selected={tools} onChange={(next) => setTools(next.includes('none') && !tools.includes('none') ? ['none'] : next.filter((id) => id !== 'none' || next.length === 1))} />
      {goals.length ? (
        <section className="welcome-recommendation" aria-live="polite">
          <h3>What you’ll need</h3>
          {missing.length ? <ul>{missing.map((capability) => <li key={capability}>{MISSING_ADVICE[capability]}</li>)}</ul>
            : <p>You already have what these projects need. You’ll connect it in the API keys step.</p>}
        </section>
      ) : null}
      <div className="onboarding-actions">
        <button type="button" onClick={() => onDone({ goals, tools, plan: initial?.plan ?? null })}>Continue</button>
      </div>
    </>
  );
}

/**
 * After Stripe returns. It claims nothing the server has not confirmed, and setup continues only once the
 * billing service confirms the plan (or the customer explicitly decides later on the product step).
 */
function CheckoutReturnNotice({ onConfirmed }: { onConfirmed: () => void }) {
  // Read, then kept until the server confirms, so a reload can still check this checkout again.
  const [ref] = useState(() => {
    const value = consumePendingCheckoutSessionRef();
    if (value) rememberPendingCheckoutSessionRef(value);
    return value;
  });
  const [state, setState] = useState<'checking' | 'confirmed' | 'pending'>(ref ? 'checking' : 'pending');
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!ref) return undefined;
    let active = true;
    setState('checking');
    checkoutStatus(ref)
      .then((status) => {
        if (!active) return;
        if (status.granted) { consumePendingCheckoutSessionRef(); setState('confirmed'); onConfirmed(); } else setState('pending');
      })
      .catch(() => { if (active) setState('pending'); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, attempt]);
  if (state === 'checking') return <p role="status">Back from checkout. Checking your payment with our billing service…</p>;
  if (state === 'confirmed') return <p role="status">Payment confirmed: your plan is active.</p>;
  return (
    <div role="status">
      <p>Back from checkout. Setup continues as soon as the payment is confirmed; this usually takes a few
        seconds. {ref ? '' : 'This browser has no record of the checkout, so check Billing.'}</p>
      {ref ? <div className="onboarding-actions">
        <button type="button" className="secondary" onClick={() => setAttempt((n) => n + 1)}>Check again</button>
      </div> : null}
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return <><dt>{label}</dt><dd>{value}</dd></>;
}

/** F-20: what was set up and what was skipped, before leaving Welcome. */
function SetupSummary({ snapshot, details }: { snapshot: OnboardingSnapshot | null; details: OnboardingDetails | null }) {
  const answers = details?.answers ?? null;
  const goalLabels = WELCOME_GOALS.filter((goal) => answers?.goals.includes(goal.id)).map((goal) => goal.label);
  const plan = details?.subscribed ? 'Plan active'
    : answers?.plan === 'checkout' ? 'Checkout started; the plan appears under Billing once payment is confirmed'
      : answers?.plan === 'later' ? 'Decided later (free features for now)' : 'Not chosen yet';
  const keys = details?.keyProviders.length
    ? `${details.keyProviders.length} stored (${[...new Set(details.keyProviders)].join(', ')})`
    : snapshot?.keys === 'unknown' ? 'Could not be checked' : 'None yet: add one when you need it';
  return (
    <dl className="welcome-summary">
      <SummaryRow label="Terms" value={snapshot?.terms === 'complete'
        ? `Accepted${details?.termsVersion ? ` (version ${details.termsVersion})` : ''}`
        : snapshot?.terms === 'incomplete' ? 'Not accepted yet' : 'Could not be checked'} />
      <SummaryRow label="What you’re making" value={goalLabels.length ? goalLabels.join(', ') : 'Skipped'} />
      <SummaryRow label="Product" value={plan} />
      <SummaryRow label="Storage" value={snapshot?.storage === 'complete' ? 'Defaults confirmed'
        : snapshot?.storage === 'incomplete' ? 'Skipped: cloud storage is used' : 'Could not be checked'} />
      <SummaryRow label="API keys" value={keys} />
      <SummaryRow label="Subscriptions" value={snapshot?.devices === 'complete' ? 'Desktop app connected'
        : snapshot?.devices === 'incomplete' ? 'Skipped (comes with the desktop app)' : 'Could not be checked'} />
    </dl>
  );
}

export function OnboardingPage({ sources }: { sources?: Partial<OnboardingSources> } = {}) {
  const sessionState = useContext(SessionContext);
  const orgKey = sessionState?.session?.org_id || 'signed-in';
  // This session's answers, for a browser that cannot store them (a refresh must not lose them).
  const sessionAnswers = useRef<WelcomeAnswers | null>(null);
  const effectiveSources = useMemo<OnboardingSources>(() => ({
    ...SERVER_SOURCES,
    // After a save attempt this session's answers win: the stored copy may be older if writes failed.
    answers: async () => sessionAnswers.current ?? readWelcomeAnswers(orgKey),
    ...sources,
  }), [sources, orgKey]);
  const [snapshot, setSnapshot] = useState<OnboardingSnapshot | null>(null);
  const [details, setDetails] = useState<OnboardingDetails | null>(null);
  const [termsPassed, setTermsPassed] = useState(false);
  const search = useSearch();
  const [location, navigate] = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const requested = params.get('step');
  const returnTo = useMemo(() => safeReturnTo(search), [search]);
  // Checkout now happens on the product step, before setup; Welcome itself always ends at Projects.
  const destination = returnTo === '/billing/checkout' || returnTo === '/welcome' ? '/projects' : returnTo;

  useEffect(() => {
    let active = true;
    loadOnboarding(effectiveSources).then((loaded) => {
      if (!active) return;
      setSnapshot(loaded.snapshot);
      setDetails(loaded.details);
    });
    return () => { active = false; };
  }, [effectiveSources]);

  const answers = details?.answers ?? null;
  const [refreshes, setRefreshes] = useState(0);
  useEffect(() => {
    if (refreshes === 0) return undefined;
    let active = true;
    loadOnboarding(effectiveSources).then((loaded) => {
      if (!active) return;
      setSnapshot(loaded.snapshot);
      setDetails(loaded.details);
    });
    return () => { active = false; };
  }, [refreshes, effectiveSources]);
  const skipped: OnboardingStep[] = answers && !subscriptionsRelevant(answers.tools) ? ['subscriptions'] : [];
  /** F-40: no setup step (storage, keys, subscriptions, done) opens before the product step is decided. */
  function gateStep(step: OnboardingStep): OnboardingStep {
    const setup = step === 'storage' || step === 'keys' || step === 'subscriptions' || step === 'done';
    if (!setup || snapshot?.plan === 'complete') return step;
    return snapshot?.goals === 'complete' ? 'plan' : 'goals';
  }
  const termsNeeded = snapshot !== null && snapshot.terms !== 'complete' && !termsPassed;
  const stage: OnboardingStage = snapshot === null ? 'loading'
    : requested === 'terms' ? 'terms'
      : termsNeeded ? 'terms'
        : isStep(requested) ? gateStep(requested as OnboardingStep)
          : resumeStage(snapshot, 'goals', skipped) as OnboardingStep;

  function goTo(next: 'terms' | OnboardingStep, replace = false) {
    const nextParams = new URLSearchParams(search);
    nextParams.delete('checkout');
    nextParams.set('step', next);
    navigate(`${location}?${nextParams.toString()}`, { replace });
  }

  function update(patch: Partial<OnboardingSnapshot>, detailPatch: Partial<OnboardingDetails> = {}) {
    const next = { ...(snapshot ?? UNKNOWN), ...patch };
    setSnapshot(next);
    setDetails((current) => ({ termsVersion: null, subscribed: false, keyProviders: [], answers: null,
      pairedDeviceIds: [], ...current, ...detailPatch }));
    return next;
  }

  function saveAnswers(next: WelcomeAnswers) {
    sessionAnswers.current = next;
    saveWelcomeAnswers(orgKey, next);
    return next;
  }

  function nextAfter(step: OnboardingStep): OnboardingStep {
    const index = ORDER.indexOf(step);
    for (const candidate of ORDER.slice(index + 1)) if (!skipped.includes(candidate)) return candidate;
    return 'done';
  }

  function back(step: OnboardingStep) {
    const index = ORDER.indexOf(step);
    const previous = [...ORDER.slice(0, index)].reverse().find((candidate) => !skipped.includes(candidate));
    goTo(previous ?? 'terms');
  }

  // The summary reads fresh server facts whenever Done is entered, by a button or by browser history.
  useEffect(() => {
    if (stage === 'done') setRefreshes((n) => n + 1);
  }, [stage]);

  const statusUnavailable = snapshot !== null && Object.values(snapshot).includes('unknown');
  const needed = neededCapabilities(answers?.goals ?? []);
  const missing = missingCapabilities(answers?.goals ?? [], answers?.tools ?? []);

  if (stage === 'loading') return (
    <section className="onboarding-page">
      <header className="page-heading"><p className="eyebrow">Welcome to Auteur Cloud</p><h1>Checking your setup</h1></header>
      <p role="status">Checking what is already set up…</p>
    </section>
  );

  const backButton = (step: OnboardingStep) => (
    <button type="button" className="secondary" onClick={() => back(step)}>Back</button>
  );

  return (
    <section className="onboarding-page">
      <header className="page-heading">
        <p className="eyebrow">Welcome to Auteur Cloud</p>
        <h1>{stage === 'terms' ? 'Review terms and policies' : 'Set up your workspace'}</h1>
        {stage !== 'terms' ? <p>A few short steps. Every step can be skipped and changed later in Settings.</p> : null}
      </header>

      {statusUnavailable ? (
        <p role="status">Some setup status could not be loaded, so those steps are shown again. Anything you already saved is kept.</p>
      ) : null}

      <OnboardingProgress step={stage} snapshot={snapshot} skipped={skipped} onSelect={(item) => goTo(item)} />

      {stage === 'terms' ? (
        <TermsAcceptancePanel onContinue={(state) => {
          setTermsPassed(true);
          const next = state?.accepted ? update({ terms: 'complete' }, { termsVersion: state.currentVersion }) : (snapshot ?? UNKNOWN);
          goTo(resumeStage({ ...next, terms: 'complete' }, 'goals', skipped) as OnboardingStep);
        }} />
      ) : null}

      {stage === 'goals' ? (
        <section className="onboarding-step" aria-labelledby="onboarding-goals-title">
          <h2 id="onboarding-goals-title">What are you here for?</h2>
          <GoalsStep initial={answers} onDone={(chosen) => {
            const saved = saveAnswers(chosen);
            update({ goals: 'complete' }, { answers: saved });
            goTo('plan');
          }} />
          <div className="onboarding-actions">
            <button type="button" className="secondary" onClick={() => goTo('terms')}>Back</button>
            <button type="button" className="secondary" onClick={() => {
              const saved = saveAnswers({ goals: [], tools: [], plan: answers?.plan ?? null });
              update({ goals: 'complete' }, { answers: saved });
              goTo('plan');
            }}>Skip these questions</button>
          </div>
        </section>
      ) : null}

      {stage === 'plan' ? (
        <section className="onboarding-step" aria-labelledby="onboarding-plan-title">
          <h2 id="onboarding-plan-title">Choose your product</h2>
          {params.get('checkout') === 'cancelled' ? <p role="status">Checkout was cancelled and nothing was charged. Choose again, or decide later.</p> : null}
          {params.get('checkout') === 'success' ? <CheckoutReturnNotice
            onConfirmed={() => setRefreshes((n) => n + 1)} /> : null}
          <ChooseProductStep recommended={recommendedProducts(answers?.goals ?? [])} alreadySubscribed={Boolean(details?.subscribed)}
            switchRequested={Boolean(details?.switchRequested)}
            onCheckoutStarted={() => {
              saveAnswers({ goals: answers?.goals ?? [], tools: answers?.tools ?? [], plan: 'checkout' });
            }}
            onDecideLater={() => {
              // An explicit "later" drops a plan picked before sign-up, so it does not reopen this step.
              clearPendingOffer();
              const saved = saveAnswers({ goals: answers?.goals ?? [], tools: answers?.tools ?? [], plan: details?.subscribed ? 'checkout' : 'later' });
              update({ plan: 'complete' }, { answers: saved });
              goTo(nextAfter('plan'));
            }} />
          <div className="onboarding-actions">{backButton('plan')}</div>
        </section>
      ) : null}

      {stage === 'storage' ? (
        <section className="onboarding-step" aria-labelledby="onboarding-storage-title">
          <h2 id="onboarding-storage-title">Storage defaults</h2>
          <p>New files are kept in Auteur’s cloud storage. Local folders on your own computer will come with the desktop
            app; you’ll be able to switch then in Settings.</p>
          <StoragePanel mode="intake" onDefaultsConfirmed={() => {
            const next = update({ storage: 'complete' });
            goTo(resumeStage(next, 'keys', skipped) as OnboardingStep);
          }} />
          <div className="onboarding-actions">
            {backButton('storage')}
            <button type="button" className="secondary" onClick={() => goTo(nextAfter('storage'))}>Skip this step</button>
          </div>
        </section>
      ) : null}

      {stage === 'keys' ? (
        <section className="onboarding-step" aria-labelledby="onboarding-keys-title">
          <h2 id="onboarding-keys-title">API keys</h2>
          <p><strong>Your tools, your choice.</strong> Auteur works with the AI providers you already use. Connect your
            own accounts and you pay them directly; we never mark up your usage. You can add or change keys any time in
            Settings.</p>
          {missing.length ? (
            <section className="welcome-recommendation">
              <h3>For what you told us you’re making</h3>
              <ul>{missing.map((capability) => <li key={capability}>{MISSING_ADVICE[capability]}</li>)}</ul>
            </section>
          ) : null}
          <GettingStartedOverview />
          <KeysPanel {...(needed.length ? { focusCapabilities: needed } : {})} onDone={() => goTo(nextAfter('keys'))} />
          <div className="onboarding-actions">
            {backButton('keys')}
            <button type="button" onClick={() => goTo(nextAfter('keys'))}>Continue</button>
            <button type="button" className="secondary" onClick={() => goTo(nextAfter('keys'))}>Skip this step</button>
          </div>
        </section>
      ) : null}

      {stage === 'subscriptions' ? (
        <section className="onboarding-step" aria-labelledby="onboarding-subscriptions-title">
          <h2 id="onboarding-subscriptions-title">Subscriptions</h2>
          <p>Already pay for ChatGPT Plus, Claude Pro or a similar plan? Auteur will be able to use those subscriptions
            through the Auteur desktop app on your computer, as well as or instead of API keys. This is optional, and the
            desktop app is <strong>coming soon</strong>, so you can skip this step for now. It is separate from your
            Auteur plan.</p>
          <details>
            <summary>Get the Auteur Bridge</summary>
            {answers?.goals?.includes('edit') ? <BridgeDownloadCard /> : null}
            <SubscriptionsPanel pairingInNewTab />
            {/* LA-12: setup-mode intake, inside the existing Local Node block, for everyone
                who reaches it. Skip stays free; no step, route or goal answer changes. */}
            <LocalAiIntake mode="setup" deviceId={details?.pairedDeviceIds?.[0] ?? null} onDownloadPlan={dispatchLocalAiPlan} />
          </details>
          <div className="onboarding-actions">
            {backButton('subscriptions')}
            <button type="button" onClick={() => goTo('done')}>Continue</button>
          </div>
        </section>
      ) : null}

      {stage === 'done' ? (
        <section className="onboarding-step" aria-labelledby="onboarding-done-title">
          <h2 id="onboarding-done-title">You’re ready</h2>
          <p>Here is what you set up. Anything skipped can be done later in <Link to="/settings">Settings</Link>, and you
            can come back to this setup from Projects.</p>
          <SetupSummary snapshot={snapshot} details={details} />
          <div className="onboarding-actions">
            {backButton('done')}
            <button type="button" onClick={() => navigate(destination, { replace: true })}>
              {destination === '/projects' ? 'Go to projects' : 'Continue'}
            </button>
          </div>
        </section>
      ) : null}
    </section>
  );
}
